import { ref } from 'vue'
import axios from 'axios'
import type { ReportDto, FileDto } from '@/types'
import type { ZonedDateTime } from '@internationalized/date'

export function convertFileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = reader.result as string
      const base64 = result.split(',')[1] || ''
      resolve(base64)
    }
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}

export function useReportSubmit() {
  const submitting = ref(false)
  const success = ref(false)
  const toast = useToast()

  async function submit(
    formState: ReportDto,
    files: File[] = [],
    dateValue?: ZonedDateTime | null
  ): Promise<boolean> {
    submitting.value = true
    success.value = false

    const payload: ReportDto = { ...formState }

    if (dateValue) {
      payload.startTime = dateValue.toDate().toLocaleString('de-DE')
    }

    if (payload.type === 'bug') {
      payload.description = payload.observedBehavior || payload.description
    } else if (payload.type === 'feature') {
      payload.description = payload.businessContext || payload.description
    }

    if (files.length > 0) {
      const fileDtos: FileDto[] = []
      for (const file of files) {
        const content = await convertFileToBase64(file)
        fileDtos.push({
          name: file.name,
          mimeType: file.type || 'application/octet-stream',
          content
        })
      }
      payload.files = fileDtos
    } else {
      payload.files = []
    }

    try {
      await axios.post('/api/registry/v1/reports', payload)
      success.value = true

      const description =
        payload.type === 'bug'
          ? 'Fehlermeldung wurde erfolgreich gesendet.'
          : payload.type === 'feature'
          ? 'Vorschlag wurde erfolgreich eingereicht.'
          : 'Notfallmeldung wurde erfolgreich gesendet.'
      toast.add({
        title: 'Erfolg',
        description,
        color: 'success'
      })

      return true
    } catch (err) {
      let message = 'Fehler beim Senden des Berichts.'
      if (axios.isAxiosError(err)) {
        message = err.response?.data?.error || message
      }

      toast.add({
        title: 'Fehler',
        description: message,
        color: 'error'
      })

      throw new Error(message)
    } finally {
      submitting.value = false
    }
  }

  return {
    submitting,
    success,
    submit
  }
}
